<template>
  <!-- Signed-out Home (Welcome board). Phones: mark, wordmark, three sample
       layers, then Create account and Log in. From 700 px: the app's desktop
       Welcome, the brand over the Log in / Create account card (C7). -->
  <auth-frame
    v-if="wide"
    tab="login"
    mark
    title="Log in"
    subtitle="Welcome back. Your sessions are waiting."
  >
    <login-form />
    <template #intro>
      <ul class="wi-points">
        <li v-for="point in points" :key="point.title" class="wi-point">
          <span class="wi-icon"><cl-icon :path="point.icon" :size="20" /></span>
          <span class="wi-title">{{ point.title }}</span>
          <span class="wi-line">{{ point.line }}</span>
        </li>
      </ul>
      <a class="wi-app" :href="APP_URL" target="_blank" rel="noopener"
        >Get the app</a
      >
    </template>
  </auth-frame>

  <div v-else class="welcome">
    <welcome-rings />
    <div class="welcome-top" aria-hidden="true" />
    <span class="welcome-mark">
      <cl-mark :size="76.24" color="currentColor" aria-hidden="true" />
    </span>
    <h1 class="welcome-name">cloudloop</h1>
    <p class="welcome-tag">Make music together.</p>

    <div class="welcome-layers" aria-hidden="true">
      <div v-for="layer in layers" :key="layer.seed" class="welcome-layer">
        <span class="welcome-dot" :style="{ background: layer.color }" />
        <svg
          width="236"
          height="34"
          viewBox="0 0 236 34"
          :style="{ color: layer.color }"
        >
          <path
            :d="layer.d"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            fill="none"
          />
        </svg>
      </div>
    </div>
    <p class="welcome-line">
      Record a loop, invite friends, and stack layers live from anywhere.
    </p>

    <div class="welcome-grow" />
    <div class="welcome-actions">
      <router-link :to="signUpLink" class="cl-btn cl-btn--hero"
        >Create account</router-link
      >
      <router-link :to="loginLink" class="cl-btn welcome-big"
        >Log in</router-link
      >
    </div>
    <p class="welcome-legal">
      By continuing you agree to the
      <router-link to="/terms">Terms</router-link> and
      <router-link to="/privacy">Privacy Policy</router-link>.
    </p>
  </div>
</template>

<script>
import ClMark from "@/components/kit/ClMark.vue";
import ClIcon from "@/components/kit/ClIcon.vue";
import { APP_URL } from "@/kit/appLink.js";
import { placeById } from "@/shell/places.js";
import AuthFrame from "@/components/auth/AuthFrame.vue";
import LoginForm from "@/components/auth/LoginForm.vue";
import WelcomeRings from "@/components/auth/WelcomeRings.vue";
import { SAMPLE_LAYERS, sampleWavePath } from "@/kit/sampleWave.js";
import { authLink } from "@/kit/auth.js";

// Where the phone layout hands over to the card (plan section 2).
const WIDE = "(min-width: 700px)";

export default {
  name: "WelcomeScreen",
  components: { ClMark, ClIcon, AuthFrame, LoginForm, WelcomeRings },
  data() {
    return {
      wide: window.matchMedia(WIDE).matches,
      media: null,
      layers: SAMPLE_LAYERS.map((l) => ({
        ...l,
        d: sampleWavePath(l.seed, l.kind, 236, 34, 4),
      })),
      APP_URL,
      // What someone landing here should know (wide; the phone board says
      // it in its one line).
      points: [
        {
          title: "Jam in the app",
          line: "Record a loop, invite friends, and stack layers live.",
          icon: placeById("live").icon,
        },
        {
          title: "Listen anywhere",
          line: "Open a session someone shares and play it in your browser.",
          icon: placeById("mine").icon,
        },
        {
          title: "Sounds and packs",
          line: "Find loops to build on, free or from their creators.",
          icon: placeById("packs").icon,
        },
      ],
    };
  },
  computed: {
    signUpLink() {
      return authLink("/sign-up", this.$route);
    },
    loginLink() {
      return authLink("/login", this.$route);
    },
  },
  mounted() {
    this.media = window.matchMedia(WIDE);
    this.media.addEventListener("change", this.onMedia);
  },
  beforeUnmount() {
    if (this.media) this.media.removeEventListener("change", this.onMedia);
  },
  methods: {
    onMedia(e) {
      this.wide = e.matches;
    },
  },
};
</script>

<style scoped>
/* Wide: the intro under the card (AuthFrame's #intro). */
.wi-points {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.wi-point {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px;
  background: var(--cl-window);
  border: 1px solid var(--border-subtle);
  border-radius: var(--cl-r-xl);
  text-align: left;
}
.wi-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-bottom: 6px;
  border-radius: var(--cl-r-md);
  background: var(--cl-accent-fill);
  color: var(--cl-accent-text);
}
.wi-title {
  font: var(--cl-headline);
  color: var(--cl-label);
}
.wi-line {
  font: var(--cl-subhead);
  color: var(--cl-label-2);
}
.wi-app {
  display: block;
  margin-top: 16px;
  text-align: center;
  font: var(--cl-callout);
  font-weight: 600;
  color: var(--cl-accent-text);
  text-decoration: none;
}
.wi-app:hover {
  text-decoration: underline;
}
.welcome {
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  background: var(--cl-bg);
  color: var(--cl-label);
}
.welcome > * {
  position: relative;
}
.welcome > .welcome-rings {
  position: absolute;
}
/* The mark's top is 150 down on a board-tall phone (the board's status bar
   included); a shorter window gives some of it up. */
.welcome-top {
  flex: none;
  height: clamp(16px, calc(100dvh - 694px), 150px);
}
.welcome-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 48px;
  color: var(--cl-accent);
}
.welcome-name {
  margin: 18px 0 0;
  font: 700 40px/48px var(--cl-font-display);
  letter-spacing: -1px;
  color: var(--cl-label);
}
.welcome-tag {
  margin: 8px 0 0;
  font: 400 19px/23px var(--cl-font);
  color: var(--cl-label-70);
}
.welcome-layers {
  margin-top: 46px;
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.welcome-layer {
  height: 46px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  border-radius: var(--cl-r-xl);
  background: color-mix(in srgb, var(--cl-cell) 90%, transparent);
  border: 1px solid var(--border-subtle);
  box-sizing: border-box;
}
.welcome-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
}
.welcome-line {
  margin: 14px 0 0;
  width: 300px;
  font: var(--cl-subhead);
  line-height: 1.45;
  color: var(--cl-label-55);
  text-align: center;
}
.welcome-grow {
  flex: 1 0 24px;
}
.welcome-actions {
  width: 100%;
  padding: 0 20px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
}
.welcome-actions .cl-btn {
  width: 100%;
  text-decoration: none;
}
/* Log in: tinted at the hero size (52, 17, radius 14). */
.cl-btn.welcome-big {
  height: var(--cl-hero-h) !important;
  font-size: 17px !important;
  border-radius: var(--cl-r-2xl) !important;
}
/* The bottom-pinned actions clear the session player bar while one shows
   (a guest who played a shared session, then came Home). */
.welcome-legal {
  margin: 0;
  padding: 0 32px calc(40px + var(--cl-player-h, 0px));
  font: var(--cl-caption);
  line-height: 1.45;
  color: var(--cl-label-45);
  text-align: center;
}
.welcome-legal a {
  color: var(--cl-accent-text);
  text-decoration: underline;
}
</style>
